// Run-over-run trend of the headline rates for one profile — inline SVG, no
// chart library. Each metric is a line over the completed runs (x = run,
// oldest left); a run where the metric is undefined (no writes, no data
// turns) leaves a gap rather than a fake zero.

import type { ProfileHeadline, TrendPoint } from '../../services/kbEffectiveness';
import styles from '../KBEffectivenessPage.module.css';

interface Metric {
  key: keyof ProfileHeadline;
  label: string;
  color: string;
}

export const TREND_METRICS: Metric[] = [
  { key: 'write_error_rate', label: 'write error rate', color: '#dc2626' },
  { key: 'kb_before_sql_rate', label: 'KB before SQL', color: '#2563eb' },
  { key: 'playbook_list_before_sql_rate', label: 'playbook list first', color: '#7c3aed' },
  { key: 'near_dup_edit_rate', label: 'near-dups consolidated', color: '#ca8a04' },
  { key: 'correction_recorded_rate', label: 'corrections recorded', color: '#16a34a' },
];

const W = 720;
const H = 220;
const PAD = { l: 40, r: 16, t: 12, b: 28 };

interface Props {
  points: TrendPoint[];
  profileId: string;
}

export function seriesFor(points: TrendPoint[], profileId: string, key: keyof ProfileHeadline): Array<number | null> {
  return points.map((p) => {
    const row = p.headline.profiles.find((h) => h.profile_id === profileId);
    const v = row ? row[key] : null;
    return typeof v === 'number' ? v : null;
  });
}

export function TrendChart({ points, profileId }: Props) {
  const n = points.length;
  if (n === 0) {
    return <p className={styles.empty}>No completed run yet — the trend starts with the first one.</p>;
  }
  const x = (i: number) => (n === 1 ? (W - PAD.l - PAD.r) / 2 + PAD.l : PAD.l + (i * (W - PAD.l - PAD.r)) / (n - 1));
  const y = (v: number) => PAD.t + (1 - Math.max(0, Math.min(1, v))) * (H - PAD.t - PAD.b);
  const ticks = [0, 0.25, 0.5, 0.75, 1];
  return (
    <figure className={styles.chart}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Headline rates over ${n} runs for ${profileId}`}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={PAD.l} x2={W - PAD.r} y1={y(t)} y2={y(t)} className={styles.gridLine} />
            <text x={PAD.l - 6} y={y(t) + 4} textAnchor="end" className={styles.axisText}>{Math.round(t * 100)}%</text>
          </g>
        ))}
        {points.map((p, i) => (
          <text key={p.run_id} x={x(i)} y={H - 8} textAnchor="middle" className={styles.axisText}>
            {p.finished_at ? p.finished_at.slice(5, 10) : '?'}
          </text>
        ))}
        {TREND_METRICS.map((m) => {
          const values = seriesFor(points, profileId, m.key);
          let d = '';
          let pen = false;
          values.forEach((v, i) => {
            if (v === null) { pen = false; return; }
            d += `${pen ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)} `;
            pen = true;
          });
          return (
            <g key={m.key} data-metric={m.key}>
              {d ? <path d={d.trim()} fill="none" stroke={m.color} strokeWidth={2} /> : null}
              {values.map((v, i) => (v === null ? null : (
                <circle key={points[i].run_id} cx={x(i)} cy={y(v)} r={3.5} fill={m.color}>
                  <title>{`${m.label}: ${Math.round(v * 100)}% (${points[i].finished_at?.slice(0, 10) ?? '?'})`}</title>
                </circle>
              )))}
            </g>
          );
        })}
      </svg>
      <figcaption className={styles.legend}>
        {TREND_METRICS.map((m) => (
          <span key={m.key} className={styles.legendItem}>
            <i style={{ background: m.color }} /> {m.label}
          </span>
        ))}
      </figcaption>
    </figure>
  );
}
